// Thème Maison : sélecteur d'offres en cartes (bloc mx-offer-picker).
// - Mode « paliers » : la carte choisie règle la quantité du formulaire produit d'Horizon.
//   Option « une variante par article » : l'ajout au panier envoie une ligne par variante.
// - Mode « variantes » : la carte choisie pilote le sélecteur de variantes d'Horizon,
//   qui met lui-même à jour prix, photo, adresse et champ `id` du formulaire.
import { addItems, formatMoney } from '@theme/mx-cart';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {{id: number, price: number, compare: number, available: boolean, options: string[]}} MxVariant
 * @typedef {{selected: number, options: string[], variants: MxVariant[]}} MxData
 */

class MxOfferPicker extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {MxData} */
  #data = { selected: 0, options: [], variants: [] };

  /** Identifiant de la variante actuellement choisie dans Horizon. */
  #variantId = 0;

  /** Position (dans la liste des cartes) du palier choisi, pour le retrouver après un morph. */
  #tierIndex = -1;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#data = this.#readData();
    this.#variantId = Number(this.#formVariantInput()?.value) || this.#data.selected;

    this.#prepareCards();

    this.addEventListener('change', this.#onChange, { signal });

    const scope = this.#scope();
    scope.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    if (this.mode === 'quantity') {
      scope.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
      if (this.perItem) {
        // La capture sur window passe avant la délégation d'Horizon (capture sur document).
        window.addEventListener('submit', this.#onSubmit, { capture: true, signal });
      }
      this.#initQuantityMode();
    } else {
      // Si la variante affichée au chargement est masquée (ex. offre retirée),
      // on sélectionne l'offre choisie par défaut, sinon la première visible.
      const visible = [...this.querySelectorAll('[data-mx-card][data-variant-id]')].filter(
        (card) => card instanceof HTMLElement && !card.closest('[hidden]') && getComputedStyle(card).display !== 'none'
      );
      const current = visible.find((card) => card.dataset.variantId === String(this.#variantId));
      if (!current && visible.length) {
        const preferred = visible.find((card) => card.querySelector('[data-mx-default]') || card.matches('[data-mx-default]')) ?? visible[0];
        const id = /** @type {HTMLElement} */ (preferred).dataset.variantId;
        this.#checkVariantCard(id);
        if (id) {
          // Le formulaire porte tout de suite l'offre cochée : un clic immédiat sur « Ajouter »
          // n'ajoute jamais la 1re variante de la fiche (ex. un kit masqué).
          const input = this.#formVariantInput();
          if (input) input.value = String(id);
          this.#syncDisplay(id);
          // setTimeout et non requestAnimationFrame : ce dernier ne part pas dans un onglet en arrière-plan.
          setTimeout(() => this.#selectVariant(id), 0);
        }
      } else {
        this.#checkVariantCard(this.#variantId);
      }
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get mode() {
    return this.dataset.mode === 'variants' ? 'variants' : 'quantity';
  }

  get perItem() {
    return this.dataset.perItem === 'true';
  }

  /* ---------------------------------------------------------------- */
  /* Lecture du DOM                                                    */
  /* ---------------------------------------------------------------- */

  /** @returns {MxData} */
  #readData() {
    const script = this.querySelector('script[data-mx-data]');
    try {
      const parsed = JSON.parse(script?.textContent || '{}');
      return {
        selected: Number(parsed.selected) || 0,
        options: Array.isArray(parsed.options) ? parsed.options : [],
        variants: Array.isArray(parsed.variants) ? parsed.variants : [],
      };
    } catch {
      return { selected: 0, options: [], variants: [] };
    }
  }

  /** Section (ou fenêtre d'achat rapide) qui contient le bloc. */
  #scope() {
    return /** @type {HTMLElement} */ (this.closest('.shopify-section, dialog') ?? document.body);
  }

  /** @returns {HTMLElement | null} */
  #productForm() {
    const productId = this.dataset.productId;
    return this.#scope().querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /** @returns {HTMLInputElement | null} */
  #formVariantInput() {
    return this.#productForm()?.querySelector('input[name="id"]') ?? null;
  }

  /** @returns {HTMLElement | null} */
  #variantPicker() {
    const productId = this.dataset.productId;
    return this.#scope().querySelector(`variant-picker[data-product-id="${productId}"]`);
  }

  /** Cartes réellement affichées, dans l'ordre du DOM. */
  #cards() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-mx-card]')).filter(
        (card) => card instanceof HTMLElement && getComputedStyle(card).display !== 'none'
      )
    );
  }

  /** @param {HTMLElement} card */
  #input(card) {
    return /** @type {HTMLInputElement | null} */ (card.querySelector('.mx-offer__input'));
  }

  /** @param {number | string} id */
  #variant(id) {
    return this.#data.variants.find((variant) => String(variant.id) === String(id));
  }

  /* ---------------------------------------------------------------- */
  /* Préparation                                                       */
  /* ---------------------------------------------------------------- */

  #prepareCards() {
    const name = `mx-offer-${this.dataset.blockId}`;
    for (const input of this.querySelectorAll('.mx-offer__input')) {
      if (input instanceof HTMLInputElement) input.name = name;
    }

    if (this.mode !== 'variants') return;

    // Les cartes enrichies (sous-blocs) arrivent après les cartes simples :
    // on remet l'ordre des variantes dans le DOM pour que le clavier suive l'ordre affiché.
    const list = this.querySelector('[data-mx-list]');
    if (!list) return;
    const cards = /** @type {HTMLElement[]} */ (Array.from(list.querySelectorAll(':scope > [data-mx-card]')));
    const sorted = [...cards].sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order));
    const alreadySorted = sorted.every((card, index) => card === cards[index]);
    if (!alreadySorted) {
      for (const card of sorted) list.appendChild(card);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Événements                                                        */
  /* ---------------------------------------------------------------- */

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-mx-item-select]')) {
      this.#updatePrices();
      return;
    }

    if (!(target instanceof HTMLInputElement) || !target.classList.contains('mx-offer__input')) return;
    const card = /** @type {HTMLElement | null} */ (target.closest('[data-mx-card]'));
    if (!card) return;

    this.#hideError();

    if (this.mode === 'quantity') {
      this.#tierIndex = this.#cards().indexOf(card);
      this.#applyQuantity(Number(card.dataset.quantity) || 1);
      this.#renderItems();
      if (this.perItem) this.#updatePrices();
    } else if (card.dataset.variantId) {
      this.#selectVariant(card.dataset.variantId);
    }
  };

  /** Changement de variante dans Horizon (sélecteur d'origine ou nos cartes). */
  #onProductSelect = (/** @type {any} */ event) => {
    const source = event.target instanceof Element ? event.target : null;
    if (source?.closest('product-card')) return;

    event.promise
      ?.then((/** @type {any} */ result) => {
        const detail = result?.detail;
        if (detail?.productId && String(detail.productId) !== String(this.dataset.productId)) return;
        const resource = detail?.resource;
        if (resource?.id) {
          this.#variantId = Number(resource.id);
          this.#syncVariantPrice(resource);
        }
        // Horizon a pu re-rendre (morph) une partie de la page : on remet l'état visuel.
        setTimeout(() => {
          this.#restore(resource?.id ? Number(resource.id) : undefined);
          if (this.mode !== 'quantity') this.#syncDisplay(this.#variantId);
        }, 0);
      })
      .catch(() => {});
  };

  /** Le client a changé la quantité avec le sélecteur d'origine. */
  #onQuantityUpdate = (/** @type {any} */ event) => {
    const form = this.#productForm();
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
    const quantity = Number(event.detail?.quantity) || 0;
    const cards = this.#cards();
    const match = cards.find((card) => Number(card.dataset.quantity) === quantity);

    for (const card of cards) {
      const input = this.#input(card);
      if (input) input.checked = card === match;
    }
    this.#tierIndex = match ? cards.indexOf(match) : -1;
    this.#renderItems();
    if (this.perItem) this.#updatePrices();
  };

  /** Ajout au panier avec une variante par article : plusieurs lignes en un envoi. */
  #onSubmit = (/** @type {SubmitEvent} */ event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const productForm = this.#productForm();
    if (!productForm || form.closest('product-form-component') !== productForm) return;

    const ids = this.#itemSelects()
      .map((select) => Number(select.value))
      .filter(Boolean);
    if (ids.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    /** @type {Map<number, number>} */
    const grouped = new Map();
    for (const id of ids) grouped.set(id, (grouped.get(id) ?? 0) + 1);
    const items = [...grouped].map(([id, quantity]) => ({ id, quantity }));

    const button = /** @type {HTMLButtonElement | null} */ (productForm.querySelector('[ref="addToCartButton"]'));
    if (button) button.setAttribute('aria-busy', 'true');
    this.#hideError();

    addItems(items, productForm, { context: 'product' })
      .catch((error) => this.#showError(error?.message))
      .finally(() => button?.removeAttribute('aria-busy'));
  };

  /* ---------------------------------------------------------------- */
  /* Mode paliers de quantité                                          */
  /* ---------------------------------------------------------------- */

  #initQuantityMode() {
    const cards = this.#cards();
    if (cards.length === 0) return;

    // Carte cochée (retour arrière du navigateur), sinon la présélection, sinon la première.
    const current =
      cards.find((card) => this.#input(card)?.checked) ??
      cards.find((card) => this.#input(card)?.hasAttribute('data-mx-default')) ??
      cards[0];
    if (!current) return;

    for (const card of cards) {
      const input = this.#input(card);
      if (input) input.checked = card === current;
    }
    this.#tierIndex = cards.indexOf(current);
    this.#applyQuantity(Number(current.dataset.quantity) || 1);
    // Les prix viennent du Liquid : on ne les recalcule qu'au changement de variante.
    this.#renderItems();
  }

  /** Carte palier choisie. */
  #selectedTier() {
    return this.#cards().find((card) => this.#input(card)?.checked) ?? null;
  }

  /**
   * Met la quantité dans le formulaire produit d'Horizon.
   * @param {number} quantity
   */
  #applyQuantity(quantity) {
    const productForm = this.#productForm();
    if (!productForm) return;

    const input = /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="quantity"]'));
    if (input && !input.hasAttribute('data-mx-offer-quantity')) {
      input.value = String(quantity);
      const selector = /** @type {any} */ (input.closest('quantity-selector-component'));
      selector?.updateButtonStates?.();
      return;
    }

    // Pas de sélecteur de quantité dans la fiche : champ caché lu à l'envoi du formulaire.
    const form = productForm.querySelector('form');
    if (!form) return;
    let hidden = input;
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      hidden.setAttribute('data-mx-offer-quantity', '');
      form.appendChild(hidden);
    }
    hidden.value = String(quantity);
  }

  /** @returns {HTMLSelectElement[]} */
  #itemSelects() {
    if (!this.perItem) return [];
    return /** @type {HTMLSelectElement[]} */ (Array.from(this.querySelectorAll('[data-mx-item-select]')));
  }

  /** Affiche un petit sélecteur de variante par article du palier choisi. */
  #renderItems() {
    if (!this.perItem) return;
    const wrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-mx-items]'));
    const list = this.querySelector('[data-mx-items-list]');
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-mx-item-template]'));
    if (!wrapper || !list || !template) return;

    const tier = this.#selectedTier();
    const count = tier ? Number(tier.dataset.quantity) || 1 : 0;
    wrapper.hidden = count === 0;

    const rows = Array.from(list.children);
    for (const row of rows.slice(count)) row.remove();

    const label = this.dataset.itemLabel || '';
    for (let index = rows.length; index < count; index++) {
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const select = /** @type {HTMLSelectElement | null} */ (fragment.querySelector('[data-mx-item-select]'));
      const labelElement = /** @type {HTMLLabelElement | null} */ (fragment.querySelector('[data-mx-item-label]'));
      const id = `MxOfferItem-${this.dataset.blockId}-${index}`;
      if (select) {
        select.id = id;
        this.#setSelectValue(select, this.#variantId);
      }
      if (labelElement) {
        labelElement.htmlFor = id;
        labelElement.textContent = `${label} ${index + 1}`.trim();
      }
      list.appendChild(fragment);
    }
  }

  /**
   * Choisit une variante dans un petit sélecteur, ou la première disponible si elle n'y est pas.
   * @param {HTMLSelectElement} select
   * @param {number} variantId
   */
  #setSelectValue(select, variantId) {
    const options = Array.from(select.options);
    const wanted = options.find((option) => option.value === String(variantId) && !option.disabled);
    const fallback = options.find((option) => !option.disabled);
    const chosen = wanted ?? fallback;
    if (chosen) select.value = chosen.value;
  }

  /**
   * Nouveau prix de base quand la variante change dans Horizon.
   * @param {any} resource
   */
  #syncVariantPrice(resource) {
    if (this.mode !== 'quantity') return;
    const variant = this.#variant(resource.id);
    if (variant) {
      variant.price = Number(resource.price) || variant.price;
      variant.compare = Number(resource.compare_at_price) || 0;
    }
    for (const select of this.#itemSelects()) this.#setSelectValue(select, Number(resource.id));
    this.#updatePrices();
  }

  /** Recalcule les prix des paliers (variante choisie ou somme des variantes par article). */
  #updatePrices() {
    if (this.mode !== 'quantity') return;
    const base = this.#variant(this.#variantId);
    if (!base) return;

    const selectedTier = this.#selectedTier();
    const itemVariants = this.#itemSelects()
      .map((select) => this.#variant(select.value))
      .filter((variant) => variant !== undefined);

    for (const card of this.#cards()) {
      const quantity = Number(card.dataset.quantity) || 1;
      const discount = Number(card.dataset.discount) || 0;

      let full = base.price * quantity;
      let regular = Math.max(base.compare, base.price) * quantity;
      if (card === selectedTier && itemVariants.length === quantity) {
        full = itemVariants.reduce((sum, variant) => sum + (variant?.price ?? 0), 0);
        regular = itemVariants.reduce((sum, variant) => sum + Math.max(variant?.compare ?? 0, variant?.price ?? 0), 0);
      }

      const final = Math.round((full * (100 - discount)) / 100);
      let strike = 0;
      if (discount > 0) strike = full;
      else if (regular > full) strike = regular;

      this.#renderPrice(card, final, strike);
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {number} final
   * @param {number} strike
   */
  #renderPrice(card, final, strike) {
    const price = card.querySelector('[data-mx-price]');
    const compareWrap = /** @type {HTMLElement | null} */ (card.querySelector('[data-mx-compare-wrap]'));
    const compare = card.querySelector('[data-mx-compare]');
    const saveWrap = /** @type {HTMLElement | null} */ (card.querySelector('[data-mx-save-wrap]'));
    const save = card.querySelector('[data-mx-save]');
    const saving = strike - final;

    if (price) price.textContent = formatMoney(final);
    if (compare) compare.textContent = formatMoney(strike);
    if (compareWrap) compareWrap.hidden = !(strike > final);
    if (save) save.textContent = formatMoney(Math.max(saving, 0));
    if (saveWrap) saveWrap.hidden = !(saving > 0);
  }

  /* ---------------------------------------------------------------- */
  /* Mode variantes en cartes                                          */
  /* ---------------------------------------------------------------- */

  /**
   * Coche la carte de la variante, sans rien déclencher.
   * @param {number} variantId
   */
  #checkVariantCard(variantId) {
    // Une variante peut avoir 2 cartes (simple + enrichie, l'une masquée) : on coche la visible.
    const cards = [...this.querySelectorAll('[data-mx-card][data-variant-id]')].filter(
      (card) => card instanceof HTMLElement
    );
    const matches = cards.filter((card) => card.dataset.variantId === String(variantId));
    const target = matches.find((card) => getComputedStyle(card).display !== 'none') ?? matches[0];
    for (const card of cards) {
      const input = this.#input(/** @type {HTMLElement} */ (card));
      if (input) input.checked = card === target;
    }
  }

  /**
   * Prix du haut de fiche et barre collante : on recopie la carte cochée.
   * Horizon ne les met pas toujours à jour (accueil, re-rendu partiel), le client verrait un autre prix.
   * @param {number | string} variantId
   */
  #syncDisplay(variantId) {
    const variant = this.#variant(variantId);
    if (!variant) return;
    const card = [...this.querySelectorAll('[data-mx-card][data-variant-id]')].find(
      (el) => el instanceof HTMLElement && el.dataset.variantId === String(variantId) && getComputedStyle(el).display !== 'none'
    );
    const price = card?.querySelector('[data-mx-price]')?.textContent?.trim();
    const scope = this.#scope();
    if (price && !variant.compare) {
      for (const el of scope.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"] .price`)) {
        el.innerHTML = `<bdi>${price}</bdi>`;
      }
    }
    const sticky = scope.querySelector('sticky-add-to-cart');
    if (sticky) {
      const label = sticky.querySelector('.sticky-add-to-cart__variant');
      if (label) label.textContent = variant.options.join(' / ');
      if (price) for (const el of sticky.querySelectorAll('.price')) el.innerHTML = `<bdi>${price}</bdi>`;
      sticky.dataset.currentVariantId = String(variant.id);
    }
  }

  /**
   * Sélectionne réellement la variante dans Horizon, via son sélecteur de variantes.
   * @param {string} variantId
   */
  #selectVariant(variantId) {
    const variant = this.#variant(variantId);
    if (!variant) return;
    this.#variantId = variant.id;
    this.#syncDisplay(variant.id);

    const picker = this.#variantPicker();
    if (!picker) {
      this.#selectVariantWithoutPicker(variant);
      return;
    }

    /** @type {HTMLElement[]} */
    const changed = [];
    this.#data.options.forEach((optionName, index) => {
      const value = variant.options[index];
      if (value === undefined) return;

      const radio = /** @type {HTMLInputElement | undefined} */ (
        Array.from(picker.querySelectorAll('fieldset input[type="radio"]')).find(
          (input) =>
            input instanceof HTMLInputElement && input.dataset.optionName === optionName && input.value === value
        )
      );
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          changed.push(radio);
        }
        return;
      }

      const select = /** @type {HTMLSelectElement | undefined} */ (
        Array.from(picker.querySelectorAll('select')).find(
          (element) =>
            element instanceof HTMLSelectElement && element.options[0]?.dataset.optionName === optionName
        )
      );
      if (select && select.value !== value) {
        select.value = value;
        for (const option of select.options) option.toggleAttribute('selected', option.value === value);
        changed.push(select);
      }
    });

    // Horizon écoute « change » sur son sélecteur : il charge la variante et met la page à jour.
    for (const element of changed) {
      element.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Sans sélecteur de variantes dans la fiche : on met au moins le bon `id` dans le formulaire.
   * @param {MxVariant} variant
   */
  #selectVariantWithoutPicker(variant) {
    const input = this.#formVariantInput();
    if (input) input.value = String(variant.id);

    const url = new URL(window.location.href);
    if (url.pathname.includes('/products/')) {
      url.searchParams.set('variant', String(variant.id));
      history.replaceState({}, '', url.toString());
    }
  }

  /* ---------------------------------------------------------------- */
  /* Après un re-rendu d'Horizon                                       */
  /* ---------------------------------------------------------------- */

  /** @param {number} [variantId] */
  #restore(variantId) {
    this.#prepareCards();
    if (this.mode === 'variants') {
      this.#checkVariantCard(variantId ?? (Number(this.#formVariantInput()?.value) || this.#variantId));
      return;
    }

    const cards = this.#cards();
    const tier = this.#tierIndex >= 0 ? cards[this.#tierIndex] : undefined;
    if (!tier) return;
    for (const card of cards) {
      const input = this.#input(card);
      if (input) input.checked = card === tier;
    }
    this.#applyQuantity(Number(tier.dataset.quantity) || 1);
    this.#renderItems();
  }

  /* ---------------------------------------------------------------- */
  /* Messages                                                          */
  /* ---------------------------------------------------------------- */

  /** @param {string} [message] */
  #showError(message) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-mx-error]'));
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  #hideError() {
    this.#showError('');
  }
}

if (!customElements.get('mx-offer-picker')) {
  customElements.define('mx-offer-picker', MxOfferPicker);
}
